import React, { useEffect, useState } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, Button, IconButton, Popover, Typography } from "@mui/material";
import dayjs from "dayjs";

const ACCENT = "#26890d";
const BAND = "rgba(38, 137, 13, 0.12)";
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const CELL = 38;

const sameDay = (a, b) => Boolean(a && b && a.isSame(b, "day"));

// One month: weekday headings and round day cells. The days between the chosen start and end sit on a soft band that
// runs across the row; the start and end are filled circles. A cell is a plain button named by its full date.
const Month = ({ month, start, end, hover, maxDate, onPick, onHover, title, left, right }) => {
  const first = month.startOf("month");
  const blanks = first.day();
  const days = Array.from({ length: month.daysInMonth() }, (_, i) => first.add(i, "day"));
  // While only a start is chosen, the days up to the pointer preview the range.
  const previewEnd = !end && start && hover && !hover.isBefore(start, "day") ? hover : end;
  return (
    <Box sx={{ width: 7 * CELL }} data-testid="range-month">
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 40, mb: 0.5 }}>
        <Box sx={{ width: 34 }}>{left}</Box>
        <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#1a1a1a" }}>{title}</Typography>
        <Box sx={{ width: 34 }}>{right}</Box>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", mb: 0.5 }}>
        {WEEKDAYS.map((day) => (
          <Typography key={day} sx={{ textAlign: "center", fontSize: "0.75rem", color: "#777", height: 28, lineHeight: "28px" }}>
            {day}
          </Typography>
        ))}
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }} role="group" aria-label={title}>
        {Array.from({ length: blanks }, (_, i) => (
          <Box key={`b${i}`} sx={{ height: CELL }} />
        ))}
        {days.map((day) => {
          const isStart = sameDay(day, start);
          const isEnd = sameDay(day, previewEnd);
          const inside = start && previewEnd && !day.isBefore(start, "day") && !day.isAfter(previewEnd, "day");
          const disabled = day.isAfter(maxDate, "day");
          const edge = isStart || isEnd;
          const weekday = day.day();
          const startOfBand = inside && (isStart || weekday === 0 || day.date() === 1);
          const endOfBand = inside && (isEnd || weekday === 6 || day.date() === day.daysInMonth());
          return (
            <Box
              key={day.format("YYYY-MM-DD")}
              sx={{
                height: CELL,
                backgroundColor: inside ? BAND : "transparent",
                borderRadius: `${startOfBand ? CELL / 2 : 0}px ${endOfBand ? CELL / 2 : 0}px ${endOfBand ? CELL / 2 : 0}px ${startOfBand ? CELL / 2 : 0}px`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                component="button"
                type="button"
                disabled={disabled}
                aria-label={day.format("MMMM D, YYYY")}
                aria-pressed={edge}
                onClick={() => onPick(day)}
                onMouseEnter={() => onHover(day)}
                sx={{
                  width: CELL - 4,
                  height: CELL - 4,
                  borderRadius: "50%",
                  border: sameDay(day, dayjs()) && !edge ? `1px solid ${ACCENT}` : "1px solid transparent",
                  backgroundColor: edge ? ACCENT : "transparent",
                  color: edge ? "#fff" : disabled ? "#ccc" : "#1a1a1a",
                  fontSize: "0.85rem",
                  fontWeight: edge ? 700 : 400,
                  fontFamily: "inherit",
                  cursor: disabled ? "default" : "pointer",
                  p: 0,
                  "&:hover": disabled || edge ? {} : { backgroundColor: "rgba(38,137,13,0.16)" },
                  "&:focus-visible": { outline: `2px solid ${ACCENT}`, outlineOffset: 1 },
                }}
              >
                {day.date()}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// A single calendar for picking a date range, two months side by side: click the first day, then the last (the days
// between are shaded, and a later click first picks a new start). Nothing is applied until Apply. Days after `maxDate`
// (today) can't be picked. `from` / `to` are "YYYY-MM-DD" strings, and so is what onApply gives back.
const RangeCalendar = ({ open, anchorEl, from, to, onApply, onClose, maxDate = dayjs() }) => {
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [hover, setHover] = useState(null);
  const [view, setView] = useState(dayjs().startOf("month"));

  // Each time it opens, start from the applied range, with its last month on the right.
  useEffect(() => {
    if (!open) return;
    const a = dayjs(from);
    const b = dayjs(to);
    setStart(a.isValid() ? a : null);
    setEnd(b.isValid() ? b : null);
    setHover(null);
    const last = b.isValid() ? b : dayjs();
    setView(last.startOf("month").subtract(1, "month"));
  }, [open, from, to]);

  const pick = (day) => {
    if (!start || (start && end)) {
      setStart(day);
      setEnd(null);
    } else if (day.isBefore(start, "day")) {
      setEnd(start);
      setStart(day);
    } else {
      setEnd(day);
    }
  };
  const right = view.add(1, "month");
  const canGoNext = right.isBefore(maxDate, "month");
  const complete = Boolean(start && end);
  const summary = start ? `${start.format("D MMM YYYY")} → ${end ? end.format("D MMM YYYY") : "pick an end date"}` : "Pick a start date";

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={{ sx: { borderRadius: "16px", boxShadow: "0 8px 30px rgba(0,0,0,0.18)", mt: 1, p: 2.5, maxWidth: "95vw" } }}
    >
      <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap", justifyContent: "center" }} onMouseLeave={() => setHover(null)}>
        <Month
          month={view}
          title={view.format("MMMM YYYY")}
          start={start}
          end={end}
          hover={hover}
          maxDate={maxDate}
          onPick={pick}
          onHover={setHover}
          left={
            <IconButton size="small" aria-label="Previous month" onClick={() => setView(view.subtract(1, "month"))}>
              <ChevronLeftIcon />
            </IconButton>
          }
        />
        <Month
          month={right}
          title={right.format("MMMM YYYY")}
          start={start}
          end={end}
          hover={hover}
          maxDate={maxDate}
          onPick={pick}
          onHover={setHover}
          right={
            <IconButton size="small" aria-label="Next month" disabled={!canGoNext} onClick={() => setView(view.add(1, "month"))}>
              <ChevronRightIcon />
            </IconButton>
          }
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mt: 2, pt: 1.5, borderTop: "1px solid #f0f0f0" }}>
        <Typography data-testid="range-summary" sx={{ fontSize: "0.85rem", color: "#555", fontWeight: 600 }}>
          {summary}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button size="small" onClick={onClose} sx={{ textTransform: "none", color: "#555", borderRadius: "16px" }}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={!complete}
            onClick={() => onApply(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"))}
            sx={{ textTransform: "none", borderRadius: "16px", backgroundColor: ACCENT, "&:hover": { backgroundColor: "#1f6e0a" }, "&:disabled": { backgroundColor: "#ccc", color: "#999" } }}
          >
            Apply
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};

export default RangeCalendar;
